import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
  type MutableRefObject,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDragDropManager } from 'react-dnd';
import { useCanvasChrome } from './canvas-chrome';
import { useMultiSelectRailHostBlockId } from './useMultiSelectRailHostBlockId';
import { useMultiSelectRailHostRowId } from './useMultiSelectRailHostRowId';
import { EmailBaseStyles } from '../render/EmailBaseStyles';
import { DocumentRenderer } from '../render/DocumentRenderer';
import type { FacilityProfile } from '../domain/facility';
import { useEmailBuilderViewport } from '../shell/emailBuilderViewportContext';
import {
  getEditorStoreState,
  useEditorActions,
  useEditorStore,
} from '../domain/store';
import { selectCanvasInteractionState } from '../domain/selectors';
import type { Selection } from '../domain/types';
import type { DragData } from '../domain/dnd/types';
import {
  resolveSelectionEdgePointerTarget,
  syncCanvasSelectionEdgeChrome,
  type ColumnSeamEdgeHighlight,
  type RowEdgeHighlight,
} from './emailBuilderSelectionEdgeStrips';
import { selectionEquals } from '../domain/selection/equals';
import { EMAIL_BUILDER_ACTION_RAIL_SELECTOR } from './actionRailConstants';
import {
  CanvasFrameChromeProvider,
  useCanvasFrameChrome,
  type CanvasFrameChromeContextValue,
} from './canvasFrameChromeContext';
import { releaseEmailBuilderCanvasKeyboardFocus } from '../shell/canvas/settings/emailBuilderSettingsFocus';

type CanvasProps = {
  facility: FacilityProfile;
};

function entityIdsForDragItem(item: DragData): string[] {
  switch (item.kind) {
    case 'item-block':
      return (item.blockIds ?? [item.blockId]).map(id => `block:${id}`);
    case 'item-row':
      return (item.rowIds ?? [item.rowId]).map(id => `row:${id}`);
    case 'item-group':
      return [`group:${item.groupId}`];
    default:
      return [];
  }
}

function syncFrameDraggingAttributes(
  canvas: HTMLElement,
  item: DragData | null,
  active: boolean,
): void {
  canvas.querySelectorAll('[data-frame-dragging]').forEach(el => {
    el.removeAttribute('data-frame-dragging');
  });
  if (!active || item === null) {
    return;
  }
  for (const entityId of entityIdsForDragItem(item)) {
    const frame = canvas.querySelector(
      `[data-entity-id="${CSS.escape(entityId)}"]`,
    );
    if (frame instanceof HTMLElement) {
      frame.setAttribute('data-frame-dragging', 'true');
    }
  }
}

function isEditorChromeTarget(target: Element): boolean {
  return (
    target.closest(
      '[data-level], [data-slot-index], [data-editor-chrome], [data-text-toolbar], [data-text-editing]',
    ) !== null
  );
}

function isBlockLevelHover(selection: Selection | null): boolean {
  return selection?.kind === 'block' || selection?.kind === 'buttonRowSlot';
}

function isInlineContentEditing(): boolean {
  return getEditorStoreState().textEditingBlock !== null;
}

function isTextEditingSurface(target: EventTarget | null): boolean {
  if (!(target instanceof Node)) {
    return false;
  }
  const element =
    target instanceof Element ? target : target.parentElement;
  return (
    element?.closest(
      '[data-text-editing="true"], [data-button-editing="true"], [data-email-builder-inline-button-label], .ProseMirror',
    ) != null
  );
}

function useCanvasFrameChromeState(
  canvasRef: RefObject<HTMLDivElement>,
  dragActiveRef: MutableRefObject<boolean>,
): CanvasFrameChromeContextValue {
  const interaction = useEditorStore(selectCanvasInteractionState);
  const { setSelection, setBlockSelection, setRowSelection } = useEditorActions();
  const [hover, setHoverState] = useState<Selection | null>(null);
  const [previewHover, setPreviewHoverState] = useState<Selection | null>(null);
  const [labelStackAnchor, setLabelStackAnchorState] =
    useState<Selection | null>(null);
  const [columnSeamEdgeHighlight, setColumnSeamEdgeHighlightState] =
    useState<ColumnSeamEdgeHighlight | null>(null);
  const [rowEdgeHighlight, setRowEdgeHighlightState] =
    useState<RowEdgeHighlight | null>(null);
  const hoverRef = useRef<Selection | null>(null);
  const previewHoverRef = useRef<Selection | null>(null);
  const labelStackAnchorRef = useRef<Selection | null>(null);
  const columnSeamEdgeHighlightRef = useRef<ColumnSeamEdgeHighlight | null>(null);
  const rowEdgeHighlightRef = useRef<RowEdgeHighlight | null>(null);
  const dndManager = useDragDropManager();

  useEffect(() => {
    const monitor = dndManager.getMonitor();
    const syncCanvasDragActiveAttribute = (active: boolean) => {
      const canvas = canvasRef.current;
      if (!canvas) {
        return;
      }
      if (active) {
        canvas.setAttribute('data-canvas-drag-active', 'true');
      } else {
        canvas.removeAttribute('data-canvas-drag-active');
      }
    };
    const unsubscribe = monitor.subscribeToStateChange(() => {
      const dragging = monitor.isDragging();
      if (dragging) {
        if (!dragActiveRef.current) {
          dragActiveRef.current = true;
          const item = (monitor.getItem() as DragData | null) ?? null;
          // Do not touch source DOM/styles or React state in dragstart — Chrome
          // aborts HTML5 drag when the source opacity/subtree mutates (~20–40ms).
          window.setTimeout(() => {
            if (!dragActiveRef.current) {
              return;
            }
            syncCanvasDragActiveAttribute(true);
            const canvas = canvasRef.current;
            if (canvas) {
              syncFrameDraggingAttributes(canvas, item, true);
            }
            hoverRef.current = null;
            previewHoverRef.current = null;
            labelStackAnchorRef.current = null;
            columnSeamEdgeHighlightRef.current = null;
            rowEdgeHighlightRef.current = null;
            setHoverState(null);
            setPreviewHoverState(null);
            setLabelStackAnchorState(null);
            setColumnSeamEdgeHighlightState(null);
            setRowEdgeHighlightState(null);
          }, 0);
        }
      } else {
        dragActiveRef.current = false;
        syncCanvasDragActiveAttribute(false);
        const canvas = canvasRef.current;
        if (canvas) {
          syncFrameDraggingAttributes(canvas, null, false);
        }
      }
    });
    return unsubscribe;
  }, [canvasRef, dndManager, dragActiveRef]);

  useEffect(() => {
    if (interaction.textEditingBlock === null) {
      return;
    }
    if (isBlockLevelHover(hoverRef.current)) {
      hoverRef.current = null;
      setHoverState(null);
    }
    previewHoverRef.current = null;
    setPreviewHoverState(null);
  }, [interaction.textEditingBlock]);

  const setColumnSeamEdgeHighlight = useCallback(
    (next: ColumnSeamEdgeHighlight | null) => {
      const current = columnSeamEdgeHighlightRef.current;
      if (
        (current === null && next === null) ||
        (current !== null &&
          next !== null &&
          current.rowId === next.rowId &&
          current.columnId === next.columnId &&
          current.edge === next.edge)
      ) {
        return;
      }
      columnSeamEdgeHighlightRef.current = next;
      setColumnSeamEdgeHighlightState(next);
    },
    [],
  );

  const setRowEdgeHighlight = useCallback((next: RowEdgeHighlight | null) => {
    const current = rowEdgeHighlightRef.current;
    if (
      (current === null && next === null) ||
      (current !== null &&
        next !== null &&
        current.rowId === next.rowId &&
        current.edge === next.edge)
    ) {
      return;
    }
    rowEdgeHighlightRef.current = next;
    setRowEdgeHighlightState(next);
  }, []);

  const setHover = useCallback((next: Selection | null) => {
    if (dragActiveRef.current) {
      return;
    }
    if (isInlineContentEditing() && next !== null && isBlockLevelHover(next)) {
      return;
    }
    const sameHover =
      next !== null &&
      hoverRef.current !== null &&
      selectionEquals(next, hoverRef.current);
    if ((next === null && hoverRef.current === null) || sameHover) {
      if (
        sameHover &&
        labelStackAnchorRef.current !== null &&
        previewHoverRef.current !== null
      ) {
        previewHoverRef.current = null;
        setPreviewHoverState(null);
      }
      return;
    }
    if (next === null || next.kind !== 'column') {
      setColumnSeamEdgeHighlight(null);
    }
    if (labelStackAnchorRef.current !== null && next !== null) {
      previewHoverRef.current = null;
      setPreviewHoverState(null);
    }
    hoverRef.current = next;
    setHoverState(next);
  }, [setColumnSeamEdgeHighlight]);

  const setPreviewHover = useCallback((next: Selection) => {
    if (dragActiveRef.current) {
      return;
    }
    if (isInlineContentEditing() && isBlockLevelHover(next)) {
      return;
    }
    previewHoverRef.current = next;
    setPreviewHoverState(next);
  }, []);

  const clearPreviewHover = useCallback(() => {
    if (!previewHoverRef.current) {
      return;
    }
    previewHoverRef.current = null;
    setPreviewHoverState(null);
  }, []);

  const openLabelStack = useCallback((anchor: Selection) => {
    if (dragActiveRef.current) {
      return;
    }
    labelStackAnchorRef.current = anchor;
    setLabelStackAnchorState(anchor);
    setPreviewHover(anchor);
  }, [setPreviewHover]);

  const closeLabelStack = useCallback(() => {
    labelStackAnchorRef.current = null;
    setLabelStackAnchorState(null);
    clearPreviewHover();
  }, [clearPreviewHover]);

  const multiSelectRailHostBlockId = useMultiSelectRailHostBlockId(
    canvasRef,
    interaction.selectedBlockIds.length > 1 ? interaction.selectedBlockIds : [],
    interaction.primaryBlockId,
  );
  const multiSelectRailHostRowId = useMultiSelectRailHostRowId(
    canvasRef,
    interaction.selectedRowIds.length > 1 ? interaction.selectedRowIds : [],
    interaction.primaryRowId,
  );

  return useMemo(
    () => ({
      ...interaction,
      multiSelectRailHostBlockId,
      multiSelectRailHostRowId,
      hover,
      previewHover,
      labelStackAnchor,
      columnSeamEdgeHighlight,
      rowEdgeHighlight,
      setSelection,
      setBlockSelection,
      setRowSelection,
      setHover,
      setColumnSeamEdgeHighlight,
      setRowEdgeHighlight,
      setPreviewHover,
      clearPreviewHover,
      openLabelStack,
      closeLabelStack,
    }),
    [
      clearPreviewHover,
      closeLabelStack,
      columnSeamEdgeHighlight,
      rowEdgeHighlight,
      hover,
      interaction,
      labelStackAnchor,
      multiSelectRailHostBlockId,
      multiSelectRailHostRowId,
      openLabelStack,
      previewHover,
      setBlockSelection,
      setColumnSeamEdgeHighlight,
      setRowEdgeHighlight,
      setHover,
      setPreviewHover,
      setRowSelection,
      setSelection,
    ],
  );
}

type CanvasSurfaceProps = {
  facility: FacilityProfile;
  canvasRef: RefObject<HTMLDivElement>;
  dragActiveRef: MutableRefObject<boolean>;
  setSelectionRef: MutableRefObject<(selection: Selection) => void>;
  closeLabelStackRef: MutableRefObject<() => void>;
};

function CanvasSurface({
  facility,
  canvasRef,
  dragActiveRef,
  setSelectionRef,
  closeLabelStackRef,
}: CanvasSurfaceProps) {
  const { t } = useTranslation();
  const { isMobileLayout } = useEmailBuilderViewport();
  const { document: emailDoc, setHover, setColumnSeamEdgeHighlight, setRowEdgeHighlight } =
    useCanvasFrameChrome();
  const { rowChrome, handleSelectionEdgeActivate } = useCanvasChrome();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const onSelectStart = (event: Event) => {
      if (dragActiveRef.current) {
        event.preventDefault();
        return;
      }
      const { textEditingBlock } = getEditorStoreState();
      if (!textEditingBlock) {
        event.preventDefault();
        return;
      }
      const target = event.target;
      if (!isTextEditingSurface(target)) {
        event.preventDefault();
      }
    };

    const clearHoverOutsideFrames = (target: EventTarget | null) => {
      if (!(target instanceof Element)) {
        return;
      }
      if (
        !target.closest('[data-level]') &&
        target.closest(EMAIL_BUILDER_ACTION_RAIL_SELECTOR) === null
      ) {
        setHover(null);
      }
    };

    const onPointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Element) || isEditorChromeTarget(target)) {
        return;
      }
      setSelectionRef.current({ kind: 'email' });
      closeLabelStackRef.current();
      releaseEmailBuilderCanvasKeyboardFocus();
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setHover(null);
        closeLabelStackRef.current();
        if (getEditorStoreState().selection.kind === 'email') {
          releaseEmailBuilderCanvasKeyboardFocus();
        }
      }
    };

    const onMouseLeave = () => {
      setHover(null);
      setColumnSeamEdgeHighlight(null);
      setRowEdgeHighlight(null);
    };

    const onPointerMove = (event: PointerEvent) => {
      if (dragActiveRef.current) {
        return;
      }
      const { selection, textEditingBlock } = getEditorStoreState();
      const chrome = syncCanvasSelectionEdgeChrome({
        clientX: event.clientX,
        clientY: event.clientY,
        selection,
        textEditingBlock,
      });
      setRowEdgeHighlight(chrome.rowEdgeHighlight);
      setColumnSeamEdgeHighlight(chrome.columnSeamEdgeHighlight);
    };

    let edgePointerHandled = false;
    let edgeMultiSelectClickPending = false;
    let edgeMultiSelectPointerDown: { x: number; y: number } | null = null;
    const edgeClickDragThresholdPx = 5;

    const suppressEvent = (event: Event) => {
      event.stopPropagation();
      event.preventDefault();
    };

    const hasSelectionModifier = (event: {
      metaKey: boolean;
      ctrlKey: boolean;
      shiftKey: boolean;
    }) => event.metaKey || event.ctrlKey || event.shiftKey;

    const resolveSelectionEdgeTargetAt = (event: {
      clientX: number;
      clientY: number;
    }) => {
      const { selection, textEditingBlock } = getEditorStoreState();
      return resolveSelectionEdgePointerTarget({
        clientX: event.clientX,
        clientY: event.clientY,
        selection,
        textEditingBlock,
      });
    };

    const resolveRowEdgePointerRowId = (event: {
      clientX: number;
      clientY: number;
    }): string | null => {
      const target = resolveSelectionEdgeTargetAt(event);
      if (target === null || 'columnId' in target) {
        return null;
      }
      return target.rowId;
    };

    const isSelectionEdgePointer = (event: {
      clientX: number;
      clientY: number;
    }) => resolveSelectionEdgeTargetAt(event) !== null;

    const onPointerDownCapture = (event: PointerEvent) => {
      if (hasSelectionModifier(event)) {
        edgePointerHandled = false;
        edgeMultiSelectClickPending = false;
        edgeMultiSelectPointerDown = null;
        if (isSelectionEdgePointer(event)) {
          suppressEvent(event);
        }
        return;
      }

      const rowEdgeRowId = resolveRowEdgePointerRowId(event);
      if (rowEdgeRowId !== null) {
        const { selectedRowIds } = getEditorStoreState();
        if (selectedRowIds.length > 1 && selectedRowIds.includes(rowEdgeRowId)) {
          edgePointerHandled = false;
          edgeMultiSelectClickPending = true;
          edgeMultiSelectPointerDown = {
            x: event.clientX,
            y: event.clientY,
          };
          return;
        }
      }

      edgeMultiSelectClickPending = false;
      edgeMultiSelectPointerDown = null;
      edgePointerHandled = handleSelectionEdgeActivate(event);
      if (edgePointerHandled) {
        suppressEvent(event);
      }
    };

    const onClickCapture = (event: MouseEvent) => {
      if (edgeMultiSelectClickPending) {
        edgeMultiSelectClickPending = false;
        const pointerDown = edgeMultiSelectPointerDown;
        edgeMultiSelectPointerDown = null;
        const movedFromPointerDown =
          pointerDown !== null &&
          (Math.abs(event.clientX - pointerDown.x) > edgeClickDragThresholdPx ||
            Math.abs(event.clientY - pointerDown.y) > edgeClickDragThresholdPx);
        const skipSelectionRefine =
          dragActiveRef.current || movedFromPointerDown;
        if (skipSelectionRefine) {
          return;
        }
        if (handleSelectionEdgeActivate(event)) {
          suppressEvent(event);
        }
        return;
      }
      if (edgePointerHandled) {
        edgePointerHandled = false;
        suppressEvent(event);
        return;
      }
      if (handleSelectionEdgeActivate(event)) {
        suppressEvent(event);
      }
    };

    const onMouseOver = (event: MouseEvent) => {
      clearHoverOutsideFrames(event.target);
    };

    const onFocusIn = (event: FocusEvent) => {
      clearHoverOutsideFrames(event.target);
    };

    canvas.addEventListener('pointerdown', onPointerDownCapture, {
      capture: true,
      passive: false,
    });
    canvas.addEventListener('click', onClickCapture, true);
    canvas.addEventListener('selectstart', onSelectStart);
    canvas.addEventListener('pointerdown', onPointerDown);
    canvas.addEventListener('keydown', onKeyDown);
    canvas.addEventListener('mouseleave', onMouseLeave);
    canvas.addEventListener('pointermove', onPointerMove);
    canvas.addEventListener('mouseover', onMouseOver);
    canvas.addEventListener('focusin', onFocusIn);

    return () => {
      canvas.removeEventListener('pointerdown', onPointerDownCapture, true);
      canvas.removeEventListener('click', onClickCapture, true);
      canvas.removeEventListener('selectstart', onSelectStart);
      canvas.removeEventListener('pointerdown', onPointerDown);
      canvas.removeEventListener('keydown', onKeyDown);
      canvas.removeEventListener('mouseleave', onMouseLeave);
      canvas.removeEventListener('pointermove', onPointerMove);
      canvas.removeEventListener('mouseover', onMouseOver);
      canvas.removeEventListener('focusin', onFocusIn);
    };
  }, [
    canvasRef,
    closeLabelStackRef,
    dragActiveRef,
    handleSelectionEdgeActivate,
    setColumnSeamEdgeHighlight,
    setHover,
    setRowEdgeHighlight,
    setSelectionRef,
  ]);

  return (
    <div
      ref={canvasRef}
      id="main-canvas"
      className="editor-canvas-scroll editor-canvas-scroll--design"
      style={{
        backgroundColor: isMobileLayout
          ? 'transparent'
          : emailDoc.settings.pageBackgroundColor,
        ...(isMobileLayout ? { height: 'auto', minHeight: 0 } : {}),
      }}
      tabIndex={-1}
      aria-label={t('Email canvas')}>
      <EmailBaseStyles />
      <div className="editor-canvas-scroll__track">
        <DocumentRenderer
          document={emailDoc}
          facility={facility}
          mergeMode="raw"
          chrome={rowChrome}
          shell="content"
          editorMode
        />
      </div>
    </div>
  );
}

export function Canvas({ facility }: CanvasProps) {
  const canvasRef = useRef<HTMLDivElement>(null);
  const dragActiveRef = useRef(false);
  const frameChrome = useCanvasFrameChromeState(canvasRef, dragActiveRef);
  const setSelectionRef = useRef(frameChrome.setSelection);
  const closeLabelStackRef = useRef(frameChrome.closeLabelStack);

  setSelectionRef.current = frameChrome.setSelection;
  closeLabelStackRef.current = frameChrome.closeLabelStack;

  return (
    <CanvasFrameChromeProvider value={frameChrome}>
      <CanvasSurface
        facility={facility}
        canvasRef={canvasRef}
        dragActiveRef={dragActiveRef}
        setSelectionRef={setSelectionRef}
        closeLabelStackRef={closeLabelStackRef}
      />
    </CanvasFrameChromeProvider>
  );
}
